import { useApolloClient } from '@apollo/client';
import { Stack } from '@mui/material';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { createPortal, flushSync } from 'react-dom';

import { SectionHeaderWithAccent } from '@/components/display/SectionHeaderWithAccent/SectionHeaderWithAccent';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { useDispositiveProvisionSuggestionsContext } from '@/modules/aiDP/context/dispositiveProvisionSuggestions.context';
import { AISuggestionsMatcherProvider } from '@/modules/aiSuggestions/AISuggestionsMatcher/context/AISuggestionsMatcher.provider';
import { GeneratingIndicator } from '@/modules/aiSuggestions/components/GeneratingIndicator';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useTestamentaryEntitySuggestionsContext } from '@/modules/aiSuggestions/context/TestamentaryEntitySuggestions.context';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useHouseholdDocumentsViewerContext } from '@/modules/documents/components/HouseholdDocumentsViewer/context/HouseholdDocumentsViewer.context';
import { SUBMIT_ERROR_MESSAGE } from '@/modules/entities/EntitySubforms/EntitySubforms.constants';
import { useIsGeneratingSuggestions } from '@/modules/estate/hooks/useIsGeneratingSuggestions';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  AugmentedUpdateTestamentaryEntityInput,
  WorkflowTrackerKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import { EditTestamentaryEntitySplitScreenFormSuggestionsMatcher } from './components/EditTestamentaryEntitySplitScreenFormSuggestionsMatcher';
import { useEditTestamentaryEntitySplitScreenModalContext } from './contexts/editTestamentaryEntitySplitScreenModal.context';
import { EditTestamentaryEntityFormShape } from './EditTestamentaryEntitySplitScreen.types';
import { EditTestamentaryEntitySection } from './EditTestamentaryEntitySplitScreen.types';
import { getUpdateTestamentaryEntityInput } from './EditTestamentaryEntitySplitScreen.utils';
import { EditTestamentaryEntitySplitScreenFooterRightActions } from './EditTestamentaryEntitySplitScreenFooter';
import { EditTestamentaryEntitySplitScreenFormSectionSwitcher } from './EditTestamentaryEntitySplitScreenFormSectionSwitcher';
import { useUpdateTestamentaryEntityAndAcknowledgeSuggestionsMutation } from './graphql/UpdateTestamentaryEntity.generated';
import {
  AccessAfterBothDeathsSection,
  AccessAfterDeathSection,
  BasicInformationSection,
  TrusteesSection,
} from './sections';

function EditTestamentaryEntitySplitScreenFormInner() {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const canEditHouseholdData = useCanEditHouseholdData();

  const client = useApolloClient();

  const { householdId } = useHouseholdDetailsContext();
  const { newlyUploadedDocumentId } = useHouseholdDocumentsViewerContext();

  const {
    testamentaryEntityId,
    currentSection,
    footerRef,
    headerRef,
    setCurrentSection,
    handleCloseModal,
    initialFormValues,
  } = useEditTestamentaryEntitySplitScreenModalContext();
  const { refetchSuggestions } = useTestamentaryEntitySuggestionsContext();
  const { refetchSuggestions: refetchDispositiveProvisionSuggestions } =
    useDispositiveProvisionSuggestionsContext();

  const handleDetailsGenerationCompleted = useCallback(() => {
    refetchSuggestions({ testamentaryEntityID: testamentaryEntityId });
  }, [refetchSuggestions, testamentaryEntityId]);

  const handleDispositiveProvisionsGenerationCompleted = useCallback(() => {
    refetchDispositiveProvisionSuggestions(testamentaryEntityId);
  }, [refetchDispositiveProvisionSuggestions, testamentaryEntityId]);

  const { isGeneratingSuggestions } = useIsGeneratingSuggestions(
    testamentaryEntityId,
    {
      detailsWorkflowTrackerKind:
        WorkflowTrackerKind.AiSuggestionsV2SubtrustDetailsGeneration,
      onDetailsCompleted: handleDetailsGenerationCompleted,
      onDispositiveProvisionsCompleted:
        handleDispositiveProvisionsGenerationCompleted,
    }
  );

  const [updateTestamentaryEntityAndSuggestions] =
    useUpdateTestamentaryEntityAndAcknowledgeSuggestionsMutation({
      onError: (error) => {
        showFeedback(SUBMIT_ERROR_MESSAGE);
        reportError('error in update testamentary entity mutation', error);
      },
    });

  const { acknowledgedSuggestions } = useSuggestionAcknowledgementContext();

  const {
    handleSubmit,
    shouldBlockNavigation,
    setShouldBlockNavigation,
    formState: { isDirty: isFormStateDirty, dirtyFields },
  } = useFormContext<EditTestamentaryEntityFormShape>();

  const isDirty = useMemo(
    () => isFormStateDirty || !isEmpty(dirtyFields),
    [dirtyFields, isFormStateDirty]
  );

  useEffect(() => {
    setShouldBlockNavigation(false);
  }, [currentSection, setShouldBlockNavigation]);

  useEffect(() => {
    if (newlyUploadedDocumentId) {
      setShouldBlockNavigation(isDirty);
    }
  }, [newlyUploadedDocumentId, setShouldBlockNavigation, isDirty]);

  const submitUpdateMutation = useCallback(
    async (
      input: AugmentedUpdateTestamentaryEntityInput,
      afterSaveAction: () => void
    ) => {
      return await updateTestamentaryEntityAndSuggestions({
        variables: {
          input,
          acknowledgeSuggestionsInput: Object.values(
            acknowledgedSuggestions
          ).map((s) => ({
            suggestionID: s.suggestionID,
            status: s.status,
            clientProfileID: s.clientProfileId,
            clientOrganizationID: s.clientOrganizationId,
            testamentaryEntityID: s.testamentaryEntityId,
          })),
        },
        onCompleted: async () => {
          showFeedback('Sub-trust updated successfully', {
            variant: 'success',
          });
          afterSaveAction();

          // Refetch in the background so section navigation is not blocked on
          // every save-and-switch interaction.
          void client.refetchQueries({ include: 'active' }).catch((error) => {
            reportError(
              'failed to refetch active queries after TE update',
              error
            );
          });
        },
      });
    },
    [
      acknowledgedSuggestions,
      client,
      reportError,
      showFeedback,
      updateTestamentaryEntityAndSuggestions,
    ]
  );

  const onValidSubmission = useCallback(
    async (
      values: EditTestamentaryEntityFormShape,
      afterSaveAction: () => void
    ) => {
      if (!testamentaryEntityId || !householdId) {
        throw new Error('Missing required testamentaryEntityId or householdId');
      }

      const input = getUpdateTestamentaryEntityInput({
        formValues: values,
        testamentaryEntityId,
        householdId,
      });

      return submitUpdateMutation(input, afterSaveAction);
    },
    [testamentaryEntityId, householdId, submitUpdateMutation]
  );

  const handleSubmitAndClose = useCallback(() => {
    flushSync(() => {
      setShouldBlockNavigation(false);
    });

    void handleSubmit(
      (values) => {
        return onValidSubmission(values, handleCloseModal);
      },
      () => {
        setShouldBlockNavigation(isDirty);
      }
    )();
  }, [
    handleCloseModal,
    handleSubmit,
    isDirty,
    onValidSubmission,
    setShouldBlockNavigation,
  ]);

  const handleSubmitAndSwitchSection = useCallback(
    (newSection: EditTestamentaryEntitySection) => {
      flushSync(() => {
        setShouldBlockNavigation(false);
      });

      void handleSubmit(
        (values) => {
          return onValidSubmission(values, () => {
            setCurrentSection(newSection);
            setShouldBlockNavigation(false);
          });
        },
        () => {
          setShouldBlockNavigation(isDirty);
        }
      )();
    },
    [
      handleSubmit,
      isDirty,
      onValidSubmission,
      setCurrentSection,
      setShouldBlockNavigation,
    ]
  );

  const handlePreSubmit = useCallback(
    (afterSubmitSection?: EditTestamentaryEntitySection) => {
      diagnostics.debug(
        `[EditTestamentaryEntitySplitScreenForm] handling submit, afterSubmitSection=${afterSubmitSection}, shouldBlockNavigation=${shouldBlockNavigation}, isDirty=${isDirty}`
      );

      if ((!isDirty && !shouldBlockNavigation) || !canEditHouseholdData) {
        if (afterSubmitSection) {
          setCurrentSection(afterSubmitSection);
        } else {
          handleCloseModal();
        }
        return;
      }

      if (afterSubmitSection) {
        handleSubmitAndSwitchSection(afterSubmitSection);
      } else {
        handleSubmitAndClose();
      }
    },
    [
      shouldBlockNavigation,
      isDirty,
      canEditHouseholdData,
      setCurrentSection,
      handleCloseModal,
      handleSubmitAndSwitchSection,
      handleSubmitAndClose,
    ]
  );

  const displayName =
    initialFormValues?.testamentaryEntity?.displayName ?? 'Sub-trust';

  const renderSection = () => {
    switch (currentSection) {
      case EditTestamentaryEntitySection.BASIC_INFORMATION:
        return <BasicInformationSection />;
      case EditTestamentaryEntitySection.ACCESS_AFTER_DEATH:
        return <AccessAfterDeathSection />;
      case EditTestamentaryEntitySection.ACCESS_AFTER_BOTH_DEATHS:
        return <AccessAfterBothDeathsSection />;
      case EditTestamentaryEntitySection.TRUSTEES:
        return <TrusteesSection />;
      default:
        return null;
    }
  };

  return (
    <>
      {headerRef.current &&
        createPortal(
          <SectionHeaderWithAccent
            containerProps={{
              pt: 1.5,
              pb: 1.5,
              pl: 2,
              pr: 2,
            }}
            header={{
              heading: displayName,
            }}
            rightHeaderContent={
              <Stack direction="row" spacing={1} alignItems="center">
                {isGeneratingSuggestions && (
                  <GeneratingIndicator dataTestId="edit-testamentary-entity-split-screen" />
                )}
                <EditTestamentaryEntitySplitScreenFormSectionSwitcher
                  onSubmit={handlePreSubmit}
                />
              </Stack>
            }
          />,
          headerRef.current
        )}
      <EditTestamentaryEntitySplitScreenFormSuggestionsMatcher />
      {renderSection()}
      {footerRef.current &&
        createPortal(
          <EditTestamentaryEntitySplitScreenFooterRightActions
            onSubmit={handlePreSubmit}
          />,
          footerRef.current
        )}
    </>
  );
}

interface EditTestamentaryEntitySplitScreenFormProps {
  initialFormValues: EditTestamentaryEntityFormShape;
}

export function EditTestamentaryEntitySplitScreenForm({
  initialFormValues,
}: EditTestamentaryEntitySplitScreenFormProps) {
  const formMethods = useForm<EditTestamentaryEntityFormShape>({
    defaultValues: initialFormValues,
  });

  return (
    <Stack
      direction="column"
      height="100%"
      p={2}
      gap={2}
      sx={{ overflowY: 'scroll' }}
    >
      <FormProvider {...formMethods}>
        <AISuggestionsMatcherProvider>
          <EditTestamentaryEntitySplitScreenFormInner />
        </AISuggestionsMatcherProvider>
      </FormProvider>
    </Stack>
  );
}
